import { ToggleTextPanel } from 'Components/molecules';
import { INFO_VARIANT } from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { Typography } from 'Components/atoms';
import { AMP_SETTINGS, AuthRole } from '@ampsec/platform-client';
import { ChangeEvent, useContext, useEffect, useMemo, useState } from 'react';
import PageErrorHandlerAndLoaderWrapper from 'Wrappers/PageErrorHandlerAndLoaderWrapper';
import './styles.scss';
import { SecurityTipsListView } from 'Components/organisms/SecurityTipsListView';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { useTenantSettings } from 'Hooks/useTenantSettings';
import { TrainingAndCoachingTabs } from 'Components/organisms/TrainingAndCoachingTabs';

const SecurityTipsPage = () => {
    const { t } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const auth = useSelector((state: RootState) => state.auth);
    const [securityTipsEnabled, setSecurityTipsEnabled] = useState(
        AMP_SETTINGS.tenant.SECURITY_TIPS_ENABLED.defaultValue,
    );
    const { tenantSettings, getAmpSettingValue, settingsLoading, refreshTenantSettings, updateAmpSetting, error } = useTenantSettings();
    // Only Owner role can edit; Admin and other roles can only view
    const isReadOnly = useMemo(() => ((auth.user.role as unknown) as AuthRole) !== AuthRole.OWNER, [auth]);

    const handleToggleSecurityTipsEnabled = async (ev: ChangeEvent<HTMLInputElement>) => {
        const newValue = ev.target.checked;
        await updateAmpSetting(AMP_SETTINGS.tenant.SECURITY_TIPS_ENABLED.id, newValue);
        setSecurityTipsEnabled(newValue);
        addToast({
            body: newValue ? t('SECURITY_TIPS_ENABLED_MESSAGE') : t('SECURITY_TIPS_DISABLED_MESSAGE'),
            severity: INFO_VARIANT,
        });
    };

    useEffect(() => {
        if (!tenantSettings || settingsLoading || Object.keys(tenantSettings).length === 0) {
            return;
        }
        // Are security tips enabled?
        const securityTipsEnabled = getAmpSettingValue(AMP_SETTINGS.tenant.SECURITY_TIPS_ENABLED.id);

        if (securityTipsEnabled === undefined) {
          updateAmpSetting(AMP_SETTINGS.tenant.SECURITY_TIPS_ENABLED.id, AMP_SETTINGS.tenant.SECURITY_TIPS_ENABLED.defaultValue, true);
        } else {
          setSecurityTipsEnabled(securityTipsEnabled as boolean);
        }
    }, [tenantSettings]);

    return (
        <PageErrorHandlerAndLoaderWrapper
            error={error}
            render={() => {
                return (
                    <div className="d-flex flex-column engagement-flow__content mt-4">
                        <div className="engagement-flow__tab-cont d-flex justify-content-between mb-4">
                            <TrainingAndCoachingTabs />
                        </div>
                        <div className="bg-text-black p-3 gap-3 d-flex card">
                            <Typography variant="body5" color="text-high-emphasis">
                                {t('SECURITY_TIPS_PAGE_SUBTITLE_1')}
                            </Typography>
                            <Typography variant="body3" color="text-high-emphasis">
                                {t('SECURITY_TIPS_PAGE_SUBTITLE_2')}
                            </Typography>

                            <div id="enable-tips-container" className="d-flex flex-column gap-3 p-3">
                                <ToggleTextPanel
                                    header={t('ENABLE_SECURITY_TIPS_LABEL')}
                                    checked={securityTipsEnabled}
                                    onToggle={handleToggleSecurityTipsEnabled}
                                    disabled={isReadOnly}
                                />
                            </div>
                            {securityTipsEnabled && <SecurityTipsListView disabled={isReadOnly} />}
                        </div>
                    </div>
                );
            }}
            isLoading={settingsLoading}
            handleRefresh={refreshTenantSettings}
        />
    );
};

export default SecurityTipsPage;
